import { Component, OnDestroy, OnInit } from '@angular/core';
import { AtlasBaseMapComponent } from '../atlas-base-map.component';
import { Feature, MapBrowserEvent } from 'ol';
import { Geometry } from 'ol/geom';
import { HttpClient } from '@angular/common/http';
import { BootstrapMediaService } from 'src/app/services/bootstrap-media.service';
import { TranslationHelperService } from 'src/app/services/translations/translation-helper.service';
import GeoJSON from 'ol/format/GeoJSON';
import { OLMapService } from 'src/app/services/ol-map/ol-map.service';
import { AtlasMobileFilterService } from 'src/app/services/filter/atlas-mobile-filter.service';
import { catchError, combineLatest, map, of, Subject, switchMap, Subscription } from 'rxjs';
import { unsubscribe } from 'src/app/utils/helpers';
import { unByKey } from 'ol/Observable';

@Component({
    selector: 'app-atlas-mobile-map',
    templateUrl: './atlas-mobile-map.component.html',
    styleUrls: ['./atlas-mobile-map.component.scss'],
})
export class AtlasMobileMapComponent extends AtlasBaseMapComponent implements OnInit, OnDestroy {
    private click$ = new Subject<MapBrowserEvent<MouseEvent>>();
    private mapSubscriptions = new Subscription();
    constructor(
        private atlasMobileFilterService: AtlasMobileFilterService,
        protected http: HttpClient,
        protected breakpointObserver: BootstrapMediaService,
        protected translationHelper: TranslationHelperService,
        public olMapService: OLMapService
    ) {
        super(http, breakpointObserver, translationHelper, olMapService);
    }

    ngOnInit() {
        this.subscriptions.push(
            combineLatest([
                this.atlasMobileFilterService.atlasMobileService.detailedSubject,
                this.atlasMobileFilterService.atlasMobileService.byzoneSubject,
            ]).subscribe((val) => {
                if (val.some((v) => v == false)) {
                    return;
                }
                this.atlasMobileFilterService.setQueryParams();
                this.atlasMobileFilterService.atlasMobileService.setDefaultFilter();
                this.loadLayer();
            })
        );
        this.atlasMobileFilterService.atlasMobileService.getLegends('mobile-legend');
        this.atlasMobileFilterService.atlasMobileService.getFilter('mobile-filter');
        this.atlasMobileFilterService.atlasMobileService.getDetails('mobile-detail');
        this.subscriptions.push(
            this.click$
                .pipe(
                    switchMap((event) => {
                        const ep = this.atlasMobileFilterService.atlasMobileService.createJoinEndpoints(event);
                        if (!ep) return of(null);

                        return ep.featureCollection.pipe(
                            map((fc) => ({ layer: ep.layer, fc })),
                            catchError(() => of({ layer: ep.layer, fc: null }))
                        );
                    })
                )
                .subscribe((result) => {
                    if (!result || !result.fc) return;
                    this.atlasMobileFilterService.atlasMobileService.removeVectorLayers();
                    const features = new GeoJSON().readFeatures(result.fc);
                    if (features.length === 1) {
                        this.addVectorLayer(features[0], new GeoJSON());
                        this.olMapService.details.push({
                            label: 'name',
                            value: features[0].get('principal_name'),
                        });
                        this.olMapService.details.push({
                            label: 'cover',
                            value: `${((features[0].get('filtervalue') as number) * 100).toFixed(2)}%`,
                        });
                    } else {
                        const reduced = this.reduceToOnePerLevel(features);
                        const statsec = reduced.statistical_sector;
                        const municipality = reduced.municipality;
                        const province = reduced.province;

                        this.addVectorLayer(statsec, new GeoJSON());
                        this.addVectorLayer(municipality, new GeoJSON(), true);

                        if (province.get('principal_name') === 'Région de Bruxelles-Capitale') {
                            this.olMapService.details.push({
                                label: 'MUNICIPALITY',
                                value: `${municipality.get('principal_name')} | ${municipality.get('alternative_name')}`,
                            });
                        } else {
                            this.olMapService.details.push({
                                label: 'MUNICIPALITY',
                                value: municipality.get('principal_name'),
                            });
                        }
                        this.olMapService.details.push({
                            label: 'name',
                            value: statsec.get('principal_name'),
                        });
                        this.olMapService.details.push({
                            label: 'cover',
                            value: `${((statsec.get('filtervalue') as number) * 100).toFixed(2)}%`,
                        });
                    }
                })
        );
        this.mapOperations();
    }
    reduceToOnePerLevel(features: Feature[]): {
        statistical_sector?: Feature;
        municipality?: Feature;
        province?: Feature;
    } {
        const byLevel: Record<string, Feature[]> = {
            statistical_sector: [],
            municipality: [],
            province: [],
        };

        features.forEach((f) => {
            const level = f.get('level');
            if (level && byLevel[level]) {
                byLevel[level].push(f);
            }
        });

        const reduce = (level: string): Feature | undefined => {
            const group = byLevel[level];
            if (!group.length) return undefined;

            const seen = new Set<string>();
            const unique = group.filter((f) => {
                const geomStr = new GeoJSON().writeGeometry(f.getGeometry());
                if (seen.has(geomStr)) return false;
                seen.add(geomStr);
                return true;
            });

            return unique[0];
        };

        return {
            statistical_sector: reduce('statistical_sector'),
            municipality: reduce('municipality'),
            province: reduce('province'),
        };
    }
    mapOperations(): void {
        this.atlasMobileFilterService.atlasMobileService.eventsKey = this.olMapService.map.on('singleclick', (event: MapBrowserEvent<MouseEvent>) => {
            this.click$.next(event);
        });
    }
    getLayerDetails(feature: Feature<Geometry>): void {
        this.atlasMobileFilterService.atlasMobileService.getLayerDetails(feature);
    }
    getLayerName(): string {
        return this.atlasMobileFilterService.atlasMobileService.getLayerName();
    }
    getStyleName(): string {
        return this.atlasMobileFilterService.atlasMobileService.getStyleName();
    }
    loadLayer() {
        this.atlasMobileFilterService.atlasMobileService.loadLayer();
    }
    setFilter() {
        this.atlasMobileFilterService.atlasMobileService.setFilter();
    }
    updateDetails(feature: Feature<Geometry>) {
        this.atlasMobileFilterService.atlasMobileService.updateDetails(feature);
    }
    updateStatSecDetails(feature: Feature<Geometry>) {
        this.atlasMobileFilterService.atlasMobileService.updateStatSecDetails(feature);
    }
    addVectorLayer(feature: Feature<Geometry>, format: GeoJSON, blackBoundries: boolean = false) {
        this.atlasMobileFilterService.atlasMobileService.addVectorLayer(feature, format, blackBoundries);
    }
    ngOnDestroy(): void {
        unByKey(this.atlasMobileFilterService.atlasMobileService.eventsKey);
        this.atlasMobileFilterService.atlasMobileService.mapOperationsUn();
        unsubscribe(this.subscriptions);
    }
}
